import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { NotificationOptions } from 'element-plus'
import type { VNode } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

/**
 * A button on a feed item that goes somewhere in the app. It holds a route
 * target rather than a click closure so it is plain data: it survives the
 * localStorage round trip, and a reloaded feed still has a working button.
 */
export interface FeedNotificationAction {
  label: string
  to: RouteLocationRaw
}

/**
 * What a caller can hand the feed. `copyText` is the plain-text payload behind
 * a "Copy" button on the item, for a message that lists things (container or
 * booking numbers) the user will want to paste somewhere else. It is separate
 * from `message` because the message is prose and often truncated on screen.
 */
export type FeedNotificationOptions = Partial<NotificationOptions> & {
  copyText?: string
  action?: FeedNotificationAction
}

export interface NotificationFeedItem {
  id: string
  title?: string
  message?: string
  copyText?: string
  action?: FeedNotificationAction
  type?: 'success' | 'warning' | 'info' | 'error'
  timestamp: DateTime
}

export interface NotificationFeedItemSerialized {
  id: string
  title?: string
  message?: string
  copyText?: string
  action?: FeedNotificationAction
  type?: 'success' | 'warning' | 'info' | 'error'
  timestamp: string // ISO string
}

/**
 * Safely extracts a string value from NotificationOptions title/message.
 * Handles VNode by converting to string or returning undefined if not serializable.
 */
function extractStringValue(
  value: string | VNode | (() => VNode) | undefined
): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'string') {
    return value
  }
  // For VNode or function returning VNode, try to extract text content
  // This is a best-effort conversion - VNodes are complex and may not have simple text
  if (typeof value === 'function') {
    try {
      const vnode = value()
      return extractVNodeText(vnode)
    } catch {
      return undefined
    }
  }
  return extractVNodeText(value)
}

/**
 * Attempts to extract text content from a VNode.
 * Returns undefined if extraction fails or VNode is not serializable.
 */
function extractVNodeText(vnode: VNode): string | undefined {
  if (!vnode) {
    return undefined
  }
  // If VNode has children that are strings, join them
  if (vnode.children) {
    if (typeof vnode.children === 'string') {
      return vnode.children
    }
    if (Array.isArray(vnode.children)) {
      const textParts = vnode.children
        .map((child) => {
          if (typeof child === 'string') {
            return child
          }
          if (typeof child === 'object' && child !== null) {
            return extractVNodeText(child as VNode)
          }
          return ''
        })
        .filter(Boolean)
      return textParts.length > 0 ? textParts.join(' ') : undefined
    }
  }
  // If VNode has text content in props or other properties, try to extract it
  // This is a fallback - VNodes are complex and may not be fully serializable
  return undefined
}

const RETENTION_MINUTES = 10
const STORAGE_KEY = 'notification_feed'

export const useNotificationFeedStore = defineStore('notificationFeed', () => {
  // Load from localStorage and filter expired notifications
  const storedNotifications = useLocalStorage<NotificationFeedItemSerialized[]>(
    STORAGE_KEY,
    [],
    {
      serializer: {
        read: (value: string) => {
          try {
            const parsed = JSON.parse(value) as NotificationFeedItemSerialized[]
            const now = DateTime.now()
            // Filter out expired notifications (older than retention period)
            return parsed.filter((item) => {
              const timestamp = DateTime.fromISO(item.timestamp)
              const age = now.diff(timestamp, 'minutes').minutes
              return age < RETENTION_MINUTES
            })
          } catch {
            return []
          }
        },
        write: (value: NotificationFeedItemSerialized[]) =>
          JSON.stringify(value),
      },
      onError: (e) => {
        console.error('Error loading notifications from local storage', e)
        localStorage.removeItem(STORAGE_KEY)
      },
    }
  )

  // Convert serialized items to NotificationFeedItem and ensure newest first
  const initialNotifications: NotificationFeedItem[] = storedNotifications.value
    .map((item) => ({
      ...item,
      timestamp: DateTime.fromISO(item.timestamp),
    }))
    .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis()) // Sort newest first

  const notifications = ref<NotificationFeedItem[]>(initialNotifications)
  const hasNewNotification = ref(false)

  // Store onClick handlers in memory (not serialized to localStorage)
  const onClickHandlers = ref<Map<string, () => void>>(new Map())

  // Sync to localStorage whenever notifications change
  function syncToStorage() {
    const serialized: NotificationFeedItemSerialized[] =
      notifications.value.map((item) => ({
        ...item,
        timestamp: item.timestamp.toISO(),
      }))
    storedNotifications.value = serialized
  }

  /**
   * Adds a notification to the feed
   * Only serializable data (strings) is stored. VNodes are converted to strings or stripped.
   * onClick handlers are stored in memory (not serialized).
   */
  function addNotification(options: FeedNotificationOptions): void {
    const id = `notification-${Date.now()}-${Math.random()}`

    // Safely extract string values, handling VNodes
    const title = extractStringValue(options.title)
    const message = extractStringValue(options.message)

    // Validate type is one of the allowed values
    const type = options.type
    const validType =
      type === 'success' ||
      type === 'error' ||
      type === 'warning' ||
      type === 'info'
        ? type
        : undefined

    // Store onClick handler in memory if provided
    if (options.onClick && typeof options.onClick === 'function') {
      onClickHandlers.value.set(id, options.onClick)
    }

    const item: NotificationFeedItem = {
      id,
      title,
      message,
      copyText: options.copyText,
      action: options.action,
      type: validType,
      timestamp: DateTime.now(),
    }
    notifications.value.unshift(item)
    hasNewNotification.value = true
    syncToStorage()

    // Auto-clear after retention period
    setTimeout(
      () => {
        removeNotification(id)
      },
      RETENTION_MINUTES * 60 * 1000
    )

    // Clear the "new" flag after a short delay (for shake animation)
    setTimeout(() => {
      hasNewNotification.value = false
    }, 500)
  }

  /**
   * Updates a notification's message in the feed
   */
  function updateNotification(
    id: string,
    updates: { message?: string; title?: string }
  ): void {
    const notification = notifications.value.find((n) => n.id === id)
    if (notification) {
      if (updates.message !== undefined) {
        notification.message = updates.message
      }
      if (updates.title !== undefined) {
        notification.title = updates.title
      }
      syncToStorage()
    }
  }

  /**
   * Removes a notification from the feed
   */
  function removeNotification(id: string): void {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
      onClickHandlers.value.delete(id)
      syncToStorage()
    }
  }

  /**
   * Clears all notifications
   */
  function clearAll(): void {
    notifications.value = []
    onClickHandlers.value.clear()
    hasNewNotification.value = false
    syncToStorage()
  }

  /**
   * Gets the onClick handler for a notification by ID
   */
  function getOnClickHandler(id: string): (() => void) | undefined {
    return onClickHandlers.value.get(id)
  }

  /**
   * Gets the most recent notification
   */
  const mostRecent = computed(() => {
    return notifications.value.length > 0 ? notifications.value[0] : null
  })

  /**
   * Gets unread count (all notifications are considered unread for simplicity)
   */
  const unreadCount = computed(() => notifications.value.length)

  return {
    notifications,
    hasNewNotification,
    mostRecent,
    unreadCount,
    addNotification,
    updateNotification,
    removeNotification,
    clearAll,
    getOnClickHandler,
  }
})
